// utils/tdsCalculator.ts

export type UITax = {
  tax_id: number;
  tax_percentage: string | number; // e.g. "5.00"
  min_amount: string | number;     // e.g. "400000.00"
  max_amount: string | number;     // e.g. "800000.00" or "0.00" for no upper limit
  status?: string;
};

export type Regime = {
  TaxRegimeID: number;
  RegimeName: string;
  AllowsStandardDeduction: 0 | 1;
  StandardDeductionAmount?: string | number;            // e.g. "75000.00"
  RebateUnderSection87A_MaxIncome?: string | number;    // e.g. "1200000.00"
  RebateUnderSection87A_Amount?: string | number;       // e.g. "120000.00"
  AllowsChapterVI_ADeductions?: 0 | 1;
};

export type SurchargeCess = {
  SurchargeCessID: number;
  HealthAndEducationCessRate_Percentage?: string | number; // e.g. "4.00"
};

/** Convert UI taxes to slab segments usable for computation. */
export function buildSlabsFromTaxes(taxes: UITax[]) {
  const normalize = (v: any) => (v === null || v === undefined ? 0 : Number(v));
  const slabs = (taxes || [])
    .filter(t => t && String(t.status || '').toLowerCase() === 'active')
    .map(t => ({
      min: normalize(t.min_amount),
      max: Number(t.max_amount) === 0 ? Infinity : normalize(t.max_amount),
      rate: normalize(t.tax_percentage) / 100,
    }))
    .sort((a, b) => a.min - b.min);

  return slabs;
}

export function calculateMonthlyTDSDynamic(params: {
  annualIncome: number;
  slabs: { min: number; max: number; rate: number }[];
  regime?: Regime | null;
  surchargeCess?: SurchargeCess | null;
  additionalDeductionsAnnual?: number;
}) {
  const {
    annualIncome,
    slabs,
    regime = null,
    surchargeCess = null,
    additionalDeductionsAnnual = 0,
  } = params;

  if (!annualIncome || annualIncome <= 0) return 0;


  const allowsStdDeduction = regime?.AllowsStandardDeduction ? Number(regime.AllowsStandardDeduction) === 1 : false;
  const stdDeduction = allowsStdDeduction ? Number(regime?.StandardDeductionAmount ?? 0) : 0;

  // taxable income cannot be negative
  const taxableIncome = Math.max(0, annualIncome - stdDeduction - Number(additionalDeductionsAnnual || 0));

  // compute slab tax (progressive)
  let tax = 0;
  for (const slab of slabs) {
    const lower = Math.max(0, taxableIncome - slab.min);
    const width = Math.max(0, Math.min(taxableIncome, slab.max) - slab.min);
    const taxableAtThisRate = Math.min(lower, width);
    if (taxableAtThisRate > 0) tax += taxableAtThisRate * slab.rate;
  }

  // 87A rebate (applies on tax before cess)
  const rebateMaxIncome = Number(regime?.RebateUnderSection87A_MaxIncome ?? 0);
  const rebateAmount = Number(regime?.RebateUnderSection87A_Amount ?? 0);
  if (rebateMaxIncome > 0 && taxableIncome <= rebateMaxIncome) {
    tax = Math.max(0, tax - rebateAmount);
  }

  // Health & Education Cess
  const cessPct = Number(surchargeCess?.HealthAndEducationCessRate_Percentage ?? 4);
  tax = tax * (1 + cessPct / 100);


  // const monthly = tax / 12;
  const monthlyTDS = Number((tax / 12).toFixed(2));

  // ✅ Return both monthly tax and taxable income
  return { monthlyTDS, taxableIncome };
}